import React, { useState } from 'react';
import { FileCode, Copy, Check, ArrowDownUp, AlertCircle } from 'lucide-react';

const Base64Converter: React.FC = () => {
  const [text, setText] = useState('');
  const [base64, setBase64] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState('');

  const encode = (input: string) => {
    try {
      setError('');
      const encoded = btoa(unescape(encodeURIComponent(input)));
      setBase64(encoded);
    } catch (e) {
      setError('Failed to encode text');
    }
  };

  const decode = (input: string) => {
    try {
      setError('');
      const decoded = decodeURIComponent(escape(atob(input)));
      setText(decoded);
    } catch (e) {
      setError('Invalid Base64 string');
    }
  };

  const handleTextChange = (value: string) => {
    setText(value);
    if (mode === 'encode') encode(value);
  };

  const handleBase64Change = (value: string) => {
    setBase64(value);
    if (mode === 'decode') decode(value);
  };

  const toggleMode = () => {
    setMode(mode === 'encode' ? 'decode' : 'encode');
    setError('');
  };

  const copyToClipboard = async (content: string, type: string) => {
    await navigator.clipboard.writeText(content);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const clearAll = () => {
    setText('');
    setBase64('');
    setError('');
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Base64 Encoder/Decoder</h2>
        <p className="text-gray-600">Encode text to Base64 or decode Base64 to text</p>
      </div>

      <div className="max-w-3xl mx-auto space-y-4">
        {/* Mode Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMode('encode')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'encode' ? 'bg-white text-emerald-600 shadow' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Encode
            </button>
            <button
              onClick={() => setMode('decode')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'decode' ? 'bg-white text-emerald-600 shadow' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Decode
            </button>
          </div>
        </div>

        {/* Input/Output */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Text Input */}
          <div className={`bg-white rounded-xl border-2 p-4 ${mode === 'encode' ? 'border-emerald-500' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">
                {mode === 'encode' ? 'Text (Input)' : 'Text (Output)'}
              </label>
              <button
                onClick={() => copyToClipboard(text, 'text')}
                className="p-1 text-gray-500 hover:text-gray-700"
                disabled={!text}
              >
                {copied === 'text' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <textarea
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
              readOnly={mode === 'decode'}
              className={`w-full h-40 px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-emerald-500 ${
                mode === 'decode' ? 'bg-gray-50' : ''
              }`}
              placeholder={mode === 'encode' ? 'Enter text to encode...' : 'Decoded text will appear here...'}
            />
          </div>

          {/* Base64 Input */}
          <div className={`bg-white rounded-xl border-2 p-4 ${mode === 'decode' ? 'border-emerald-500' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">
                {mode === 'decode' ? 'Base64 (Input)' : 'Base64 (Output)'}
              </label>
              <button
                onClick={() => copyToClipboard(base64, 'base64')}
                className="p-1 text-gray-500 hover:text-gray-700"
                disabled={!base64}
              >
                {copied === 'base64' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <textarea
              value={base64}
              onChange={(e) => handleBase64Change(e.target.value)}
              readOnly={mode === 'encode'}
              className={`w-full h-40 px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-emerald-500 ${
                mode === 'encode' ? 'bg-gray-50' : ''
              }`}
              placeholder={mode === 'decode' ? 'Enter Base64 to decode...' : 'Encoded Base64 will appear here...'}
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center gap-3">
          <button
            onClick={toggleMode}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <ArrowDownUp className="w-4 h-4" />
            Switch to {mode === 'encode' ? 'Decode' : 'Encode'}
          </button>
          <button
            onClick={clearAll}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Clear
          </button>
        </div>

        {/* Stats */}
        {(text || base64) && (
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Text length:</span>
                <span className="ml-2 font-mono">{text.length} characters</span>
              </div>
              <div>
                <span className="text-gray-500">Base64 length:</span>
                <span className="ml-2 font-mono">{base64.length} characters</span>
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
          <div className="flex items-start gap-3">
            <FileCode className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-emerald-900 mb-2">About Base64</h4>
              <p className="text-sm text-emerald-700">
                Base64 encoding converts binary data to ASCII text using 64 characters (A-Z, a-z, 0-9, +, /).
                It's commonly used for encoding data in URLs, emails, and data URIs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Base64Converter;
